export type VoiceDialogUtteranceIntent = "end" | "pause" | "resume" | "continue" | "interrupt";

/** How long the mic can still hear the speaker after TTS ends. */
export const VOICE_DIALOG_ECHO_TAIL_MS = 400;

/**
 * A short polite tail may follow a closing or yes/no phrase. Anything longer
 * is a real request and must still reach the assistant.
 */
const POLITE_TAILS = ["please", "thank you", "thanks", "for now"] as const;

/** Whole utterance, after normalization, that declines another turn. */
const CLOSING_PHRASES = new Set([
  "no",
  "nope",
  "nah",
  "no thanks",
  "no thank you",
  "thank you",
  "thanks",
  "thats all",
  "that is all",
  "thats it",
  "that is it",
  "nothing else",
  "nothing more",
  "im good",
  "i am good",
  "im done",
  "i am done",
  "not really",
  "i dont think so",
  "i do not think so",
]);

/**
 * Ends the dialog immediately. These are session commands, not answers, so
 * they never go to the assistant and do not ask for confirmation first.
 * Bare "stop" interrupts playback while the assistant is talking; it stays
 * here so it still ends the dialog on the user's listening turn (and while
 * paused).
 */
const END_NOW_PHRASES = new Set([
  "stop",
  "stop the conversation",
  "stop the dialog",
  "stop the dialogue",
  "end the conversation",
  "end the dialog",
  "end the dialogue",
  "goodbye",
  "good bye",
  "bye",
  "bye bye",
]);

/** Freezes the dialog the same way as the pause button. */
const PAUSE_PHRASES = new Set([
  "pause",
  "pause the conversation",
  "pause conversation",
  "hold on",
  "hold",
  "hang on",
  "wait a second",
  "stop talking",
]);

/**
 * Bare "stop" while the assistant is voicing a line stops playback and hands
 * the mic back to the user (barge-in). It is kept out of {@link PAUSE_PHRASES}
 * and {@link END_NOW_PHRASES} only matches it off the assistant turn, so a
 * listening-turn or paused "stop" still ends the dialog.
 */
const INTERRUPT_PHRASES = new Set(["stop"]);

/** Continues a paused dialog the same way as the resume button. */
const RESUME_PHRASES = new Set([
  "continue",
  "play",
  "resume",
  "go on",
  "keep going",
  "keep talking",
  "unpause",
]);

export function normalizeVoiceUtterance(text: string): string {
  return text
    .toLowerCase()
    .replace(/['’]/g, "")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

function matchesPhrase(normalized: string, phrases: ReadonlySet<string>): boolean {
  if (phrases.has(normalized)) {
    return true;
  }
  for (const tail of POLITE_TAILS) {
    const suffix = ` ${tail}`;
    if (normalized.endsWith(suffix) && phrases.has(normalized.slice(0, -suffix.length))) {
      return true;
    }
  }
  return false;
}

function tokensOf(normalized: string): readonly string[] {
  return normalized.length === 0 ? [] : normalized.split(" ");
}

function containsTokenWindow(haystack: readonly string[], needle: readonly string[]): boolean {
  if (needle.length === 0 || needle.length > haystack.length) {
    return false;
  }
  for (let start = 0; start <= haystack.length - needle.length; start += 1) {
    if (needle.every((token, offset) => haystack[start + offset] === token)) {
      return true;
    }
  }
  return false;
}

/**
 * True when the recognizer likely heard the assistant, not the user.
 * Uses token windows so "no" does not match "nothing" and a hold-phrase
 * fragment still counts as echo.
 */
export function isVoiceDialogEcho(transcript: string, spokenLine: string): boolean {
  const heard = normalizeVoiceUtterance(transcript);
  const spoken = normalizeVoiceUtterance(spokenLine);
  if (heard.length === 0 || spoken.length === 0) {
    return false;
  }
  const heardTokens = tokensOf(heard);
  const spokenTokens = tokensOf(spoken);
  return (
    containsTokenWindow(spokenTokens, heardTokens) || containsTokenWindow(heardTokens, spokenTokens)
  );
}

export interface VoiceDialogUtteranceContext {
  awaitingHelpOffer: boolean;
  /** The user froze the dialog. Resume phrases apply; echo does not. */
  paused?: boolean;
  /** Greeting, hold line, reply speech, or thinking. Bare "stop" interrupts. */
  assistantTurn?: boolean;
}

/**
 * Decide what a voice-dialog utterance means.
 * Interrupt, pause, resume, and an explicit end never reach the assistant.
 * A short decline after a spoken question ends the dialog straight away.
 * Any other request is sent on during the user's turn.
 */
export function classifyVoiceDialogUtterance(
  text: string,
  context: Readonly<VoiceDialogUtteranceContext>,
): VoiceDialogUtteranceIntent {
  const normalized = normalizeVoiceUtterance(text);
  if (normalized.length === 0) {
    return "continue";
  }
  if (context.paused && matchesPhrase(normalized, RESUME_PHRASES)) {
    return "resume";
  }
  // While the assistant is voicing a line, bare "stop" barges in: stop talking
  // and listen. Off the assistant turn (and while paused) "stop" ends instead.
  if (context.assistantTurn && !context.paused && matchesPhrase(normalized, INTERRUPT_PHRASES)) {
    return "interrupt";
  }
  if (matchesPhrase(normalized, PAUSE_PHRASES)) {
    return "pause";
  }
  if (matchesPhrase(normalized, END_NOW_PHRASES)) {
    return "end";
  }
  if (context.awaitingHelpOffer && matchesPhrase(normalized, CLOSING_PHRASES)) {
    return "end";
  }
  return "continue";
}
